import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { AdminPermission } from '@shared/roles';
import { ROLE_LABELS } from '@shared/roles';
import { useAuth } from './auth';
import { Badge, Button, Notice } from '../components/ui';

const NAV: { to: string; label: string; permission?: AdminPermission }[] = [
  { to: '/', label: '概览', permission: 'dashboard.view' },
  { to: '/products', label: '产品', permission: 'product.view' },
  { to: '/content', label: '内容管理', permission: 'content.view' },
  { to: '/orders', label: '订单与退款', permission: 'order.view' },
  { to: '/invoices', label: '发票', permission: 'order.view' },
  { to: '/customers', label: '客户', permission: 'customer.view' },
  { to: '/tenants', label: '组织与审核', permission: 'customer.view' },
  { to: '/tickets', label: '工单', permission: 'ticket.view' },
  { to: '/audit', label: '审计', permission: 'audit.view' },
  { to: '/settings', label: '平台开关', permission: 'settings.view' },
  { to: '/assistant', label: 'AI 客服', permission: 'settings.view' },
  { to: '/staff', label: '员工', permission: 'staff.manage' },
];

/** 后台外壳：顶栏 + 左侧导航。信息密度比用户侧高，不做花哨的分层。 */
export function AppShell({ children }: { children: ReactNode }) {
  const { session, logout, can } = useAuth();
  const navigate = useNavigate();

  const items = NAV.filter((item) => item.permission === undefined || can(item.permission));

  return (
    <div className="console-wash flex min-h-full">
      <aside className="hidden w-52 shrink-0 flex-col border-r border-line bg-panel px-3 py-4 sm:flex">
        <div className="mb-6 px-2">
          <p className="text-[15px] font-semibold text-fg">Havorix™ 运营后台</p>
          <p className="mt-0.5 text-xs text-fg-subtle">admin.havorix.cn</p>
        </div>

        <nav className="flex-1 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `block rounded-md px-3 py-2 text-[13px] transition-colors ${
                  isActive ? 'bg-brand/10 font-medium text-brand' : 'text-fg-muted hover:bg-canvas hover:text-fg'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-line pt-3">
          <NavLink to="/account" className="block rounded-md px-3 py-2 text-[13px] text-fg-muted hover:bg-canvas">
            账号与安全
          </NavLink>
          <div className="px-3">
            <p className="truncate text-[13px] font-medium text-fg">{session?.user.name}</p>
            <p className="truncate text-xs text-fg-subtle">{session?.user.email}</p>
            <div className="mt-1 flex items-center gap-1">
              <Badge tone="brand">{session ? ROLE_LABELS[session.user.role] : ''}</Badge>
              {session?.user.totpEnabled ? <Badge tone="ok">2FA</Badge> : <Badge tone="warn">未开 2FA</Badge>}
            </div>
          </div>
          <Button
            size="sm"
            block
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            退出登录
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 items-center justify-between border-b border-line bg-panel px-4 sm:hidden">
          <span className="text-[15px] font-semibold">Havorix™ 后台</span>
          <Button size="sm" onClick={async () => { await logout(); navigate('/login'); }}>
            退出
          </Button>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-line bg-panel px-2 py-1 sm:hidden">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `whitespace-nowrap rounded px-2 py-1.5 text-[13px] ${
                  isActive ? 'bg-brand/10 text-brand' : 'text-fg-muted'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1 px-4 py-5 sm:px-6">
          {session?.mustChangePassword ? (
            <div className="mb-4">
              <Notice tone="warn">
                你仍在使用初始口令。请到「账号与安全」修改后再使用后台功能（改密前接口会拒绝）。
              </Notice>
            </div>
          ) : null}
          {children}
        </main>
      </div>
    </div>
  );
}
